import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import FullscreenOverlay from './FullscreenOverlay'

/**
 * Inline result table (columns + row-major rows). Normal mode is compact and
 * client-side paginated; a full-screen mode expands the table to the whole
 * browser tab and lazy-loads rows on scroll. For audience results, rows open
 * the customer drill-down (via customer_id).
 */
const PAGE_SIZE = 10
const LAZY_CHUNK = 60

function formatCell(v) {
  if (v == null) return ''
  if (typeof v === 'number') return new Intl.NumberFormat().format(v)
  return String(v)
}

/** Enter/Space activates a row like a click (keyboard a11y). */
function activateOnKey(fn) {
  return (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      fn()
    }
  }
}

export default function ResultTable({ table, isAudience, onOpenCustomer, selectedCustomerId }) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [fullscreen, setFullscreen] = useState(false)
  const [visible, setVisible] = useState(LAZY_CHUNK)
  const [sort, setSort] = useState(null) // { col, dir: 'asc' | 'desc' }
  const fsScrollRef = useRef(null)

  const columns = table?.columns || []
  const rows = useMemo(() => table?.rows || [], [table])

  // The customer's id may be labelled `customer_id` or just `id` (values like
  // `nlq_cust_2637`). Only used to drill when the table is already a customer list.
  const customerIdCol = useMemo(() => {
    const lower = columns.map((c) => String(c).toLowerCase())
    const byCustomerId = lower.indexOf('customer_id')
    return byCustomerId !== -1 ? byCustomerId : lower.indexOf('id')
  }, [columns])
  const clickable = isAudience && customerIdCol !== -1 && typeof onOpenCustomer === 'function'
  const isNumericCol = useMemo(
    () => columns.map((_, ci) => rows.some((r) => typeof r[ci] === 'number')),
    [columns, rows],
  )

  // Client-side sort by a clicked column (empties always sorted last).
  const sortedRows = useMemo(() => {
    if (!sort) return rows
    const { col, dir } = sort
    const numeric = isNumericCol[col]
    const factor = dir === 'asc' ? 1 : -1
    return rows.slice().sort((a, b) => {
      const av = a[col]
      const bv = b[col]
      const aEmpty = av == null || av === ''
      const bEmpty = bv == null || bv === ''
      if (aEmpty && bEmpty) return 0
      if (aEmpty) return 1
      if (bEmpty) return -1
      const cmp = numeric
        ? Number(av) - Number(bv)
        : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' })
      return cmp * factor
    })
  }, [rows, sort, isNumericCol])

  const toggleSort = (col) => {
    setPage(1)
    setSort((prev) => {
      if (!prev || prev.col !== col) return { col, dir: 'asc' }
      if (prev.dir === 'asc') return { col, dir: 'desc' }
      return null
    })
  }

  // Reset the lazy window each time full screen opens (Escape + scroll-lock are
  // handled by FullscreenOverlay).
  useEffect(() => {
    if (fullscreen) setVisible(LAZY_CHUNK)
  }, [fullscreen])

  if (columns.length === 0) return null
  if (rows.length === 0) {
    return (
      <div className="fd-body-sm py-4" style={{ color: 'var(--text-tertiary)' }}>
        {t('Sentinel.V2.table.empty')}
      </div>
    )
  }

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageSlice = sortedRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
  const fsSlice = sortedRows.slice(0, visible)

  const onFsScroll = () => {
    const el = fsScrollRef.current
    if (!el) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 400) {
      setVisible((v) => Math.min(rows.length, v + LAZY_CHUNK))
    }
  }

  const headerCells = (
    <tr>
      {columns.map((c, ci) => {
        const active = sort && sort.col === ci
        const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
        const icon = active ? (sort.dir === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'
        return (
          <th key={ci} className={isNumericCol[ci] ? 'num' : undefined} aria-sort={ariaSort}>
            <button
              type="button"
              className="fd-th-sort"
              onClick={() => toggleSort(ci)}
              title={t('Sentinel.V2.table.sort')}
            >
              <span>{c}</span>
              <i className={`fa-solid ${icon}`} aria-hidden="true" />
            </button>
          </th>
        )
      })}
      {clickable && <th aria-hidden="true" style={{ width: '1%' }} />}
    </tr>
  )

  const renderRow = (row, ri) => {
    const selected =
      clickable &&
      selectedCustomerId != null &&
      String(row[customerIdCol]) === String(selectedCustomerId)
    const openCustomer = clickable ? () => onOpenCustomer(String(row[customerIdCol])) : undefined
    return (
      <tr
        key={ri}
        className={
          [clickable ? 'is-clickable' : '', selected ? 'is-selected' : '']
            .filter(Boolean)
            .join(' ') || undefined
        }
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={
          clickable ? t('Sentinel.V2.table.openCustomer', { id: row[customerIdCol] }) : undefined
        }
        onClick={openCustomer}
        onKeyDown={clickable ? activateOnKey(openCustomer) : undefined}
      >
        {columns.map((_, ci) => (
          <td key={ci} className={isNumericCol[ci] ? 'num' : undefined}>
            {formatCell(row[ci])}
          </td>
        ))}
        {clickable && (
          <td style={{ textAlign: 'right', color: 'var(--text-tertiary)' }}>
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
          </td>
        )}
      </tr>
    )
  }

  return (
    <div className="fd-tablewrap">
      <div className="flex items-center justify-end mb-2">
        <button className="fd-btn fd-btn--sm fd-btn--ghost" onClick={() => setFullscreen(true)}>
          <i className="fa-solid fa-up-right-and-down-left-from-center" aria-hidden="true" />{' '}
          {t('Sentinel.V2.table.fullScreen')}
        </button>
      </div>

      <div
        className="fd-card fd-card--flush fd-table-scroll"
        role="region"
        aria-label={t('Sentinel.V2.rows', { n: sortedRows.length.toLocaleString() })}
        tabIndex={0}
      >
        <table className="fd-table">
          <thead>{headerCells}</thead>
          <tbody>{pageSlice.map((row, i) => renderRow(row, (safePage - 1) * PAGE_SIZE + i))}</tbody>
        </table>
      </div>

      {rows.length > PAGE_SIZE && (
        <div
          className="flex items-center justify-between mt-2 fd-mono"
          style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}
        >
          <span>
            {t('Sentinel.V2.table.range', {
              from: ((safePage - 1) * PAGE_SIZE + 1).toLocaleString(),
              to: Math.min(safePage * PAGE_SIZE, sortedRows.length).toLocaleString(),
              total: sortedRows.length.toLocaleString(),
            })}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="fd-btn fd-btn--sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
            >
              {t('Sentinel.V2.table.prev')}
            </button>
            <span>
              {safePage} / {totalPages}
            </span>
            <button
              className="fd-btn fd-btn--sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
            >
              {t('Sentinel.V2.table.next')}
            </button>
          </div>
        </div>
      )}

      {fullscreen && (
        <FullscreenOverlay
          title={t('Sentinel.V2.rows', { n: sortedRows.length.toLocaleString() })}
          onClose={() => setFullscreen(false)}
          bodyRef={fsScrollRef}
          onScroll={onFsScroll}
        >
          <table className="fd-table">
            <thead>{headerCells}</thead>
            <tbody>{fsSlice.map((row, i) => renderRow(row, i))}</tbody>
          </table>
          {visible < sortedRows.length && (
            <div
              className="fd-body-sm"
              style={{ color: 'var(--text-tertiary)', padding: '12px', textAlign: 'center' }}
            >
              {t('Sentinel.V2.table.loadingMore', {
                n: visible.toLocaleString(),
                total: sortedRows.length.toLocaleString(),
              })}
            </div>
          )}
        </FullscreenOverlay>
      )}
    </div>
  )
}

ResultTable.propTypes = {
  table: PropTypes.object,
  isAudience: PropTypes.bool,
  onOpenCustomer: PropTypes.func,
  selectedCustomerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}
